import { useState } from "react";
import { gstLiveApi, type GstConnection } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { ErrorNote } from "../ui/bits";
import { fmtDate } from "../companies/CompanyPicker";

// Connecting a GSTIN to the GST portal (an OTP; six hours) — shared by the return pages (sql/232)
export function useGstConnection(reg: string) {
  const { company } = useCompany();
  return useLoad(() => gstLiveApi.connection(company.id, reg), [company.id, reg]);
}

export function ConnectGstin({ reg, c, onChange }: { reg: string; c: GstConnection; onChange: (c: GstConnection) => void }) {
  const { company } = useCompany();
  const [user, setUser] = useState(c.username ?? ""); const [otp, setOtp] = useState(""); const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  if (!c.gsp) return <p className="muted small">Filing from Books needs the GSP connection, which Altekore hasn't switched on yet. Until then, download the JSON and upload it on the portal.</p>;
  if (c.connected) return <p className="small">Connected to the GST portal as <b>{c.username}</b> until {new Date(c.expires_at!).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}.</p>;
  const go = (fn: () => Promise<void>) => { setBusy(true); setErr(null); fn().catch((x) => setErr(errText(x))).finally(() => setBusy(false)); };
  return (
    <div className="fields inline">
      {!sent ? <>
        <label>GST portal username for {c.gstin}<input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" /></label>
        <button disabled={busy || !user.trim()} onClick={() => go(async () => setSent((await gstLiveApi.connectStart(company.id, reg, user)).message))}>Send OTP</button>
        <span className="muted small">API access must be on for this GSTIN (portal › My profile › Manage API access).</span>
      </> : <>
        <span className="small">{sent}</span>
        <label>OTP<input inputMode="numeric" maxLength={6} className="mono short" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} autoFocus /></label>
        <button className="primary" disabled={busy || otp.length !== 6} onClick={() => go(async () => onChange(await gstLiveApi.connectFinish(company.id, reg, otp)))}>Connect</button>
        <button className="link" onClick={() => setSent(null)}>Back</button>
      </>}
      <ErrorNote text={err} />
    </div>
  );
}

const STEPS: Record<"gstr1" | "gstr3b", [string, string][]> = {
  gstr1: [["save", "Upload to the portal"], ["status", "Check the upload"], ["proceed", "Proceed to file"], ["status", "Check"], ["summary", "Get the summary"], ["otp", "Send the EVC OTP"], ["file", "File"]],
  gstr3b: [["save", "Upload to the portal"], ["status", "Check the upload"], ["offset", "Set off the liability"], ["otp", "Send the EVC OTP"], ["file", "File"]],
};
const NEXT: Record<string, Record<string, string>> = {
  gstr1: { new: "save", saved: "status", accepted: "proceed", errors: "save", proceeding: "status", proceeded: "summary", summary: "otp", otp_sent: "file" },
  gstr3b: { new: "save", saved: "status", accepted: "offset", errors: "save", offset: "otp", otp_sent: "file" },
};

// filing a return on the portal, a step at a time, from what Books has worked out
export function FileOnPortal({ reg, period, ret, filedOn, done }: { reg: string; period: string; ret: "gstr1" | "gstr3b"; filedOn: string | null; done: () => void }) {
  const { company } = useCompany();
  const conn = useGstConnection(reg);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [pan, setPan] = useState(""); const [otp, setOtp] = useState("");
  const c = conn.data;
  if (!c || filedOn) return null;
  const f = c.filings.find((x) => x.period === period && x.return === ret);
  if (f?.arn) return <p className="msg ok">Filed on the portal {f.filed_on ? fmtDate(f.filed_on) : ""} · ARN <span className="mono">{f.arn}</span></p>;
  const step = f?.step ?? "new";
  const next = NEXT[ret][step] ?? "save";
  const run = (s: string) => { setBusy(true); setErr(null);
    gstLiveApi.step(company.id, reg, period, ret, s, { pan: pan.toUpperCase(), otp }).then((r) => { void conn.reload(); if (r.arn) done(); })
      .catch((x) => { setErr(errText(x)); void conn.reload(); }).finally(() => setBusy(false)); };
  return (
    <section className="card panel portal-filing">
      <h3>File on the GST portal</h3>
      <ConnectGstin reg={reg} c={c} onChange={(x) => conn.setData(x)} />
      {c.connected && <>
        <ol className="steps small">{STEPS[ret].map(([s, label], i) => <li key={i} className={s === next ? "on" : ""}>{label}</li>)}</ol>
        {f?.problem && <p className="msg err small">{f.problem}</p>}
        {(next === "otp" || next === "file") && <label>Authorised signatory's PAN<input className="mono short" maxLength={10} value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} /></label>}
        {next === "file" && <label>EVC OTP<input inputMode="numeric" maxLength={6} className="mono short" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} /></label>}
        <div className="actions">
          <button className="primary" disabled={busy || ((next === "otp" || next === "file") && pan.length !== 10) || (next === "file" && otp.length !== 6)} onClick={() => run(next)}>
            {busy ? "Working…" : STEPS[ret].find(([s]) => s === next)?.[1] ?? next}</button>
          {step !== "new" && <button className="link" disabled={busy} onClick={() => run("save")}>Upload again</button>}
          {next === "file" && <button className="link" disabled={busy} onClick={() => run("otp")}>Send the OTP again</button>}
        </div>
      </>}
      <ErrorNote text={err} />
    </section>
  );
}

// the IRP and e-way bill API users of each GSTIN (made on the IRP / EWB portals under "API registration"), for live IRNs
// and e-way bills; passwords are kept where the app can't read them back
export function GstApiUsers({ can }: { can: boolean }) {
  const { company, ref } = useCompany();
  const regs = ref?.gst_registrations ?? [];
  if (!regs.length) return null;
  return (
    <div className="api-users">
      <h4>IRP and e-way bill API users</h4>
      <p className="small muted">Make an API user on the e-invoice and e-way bill portals ("API registration", choosing Sandbox as the GSP), then enter it here to get IRNs and e-way bills straight from Books.</p>
      {regs.map((r) => <ApiUserRow key={r.id} companyId={company.id} reg={r.id} gstin={r.gstin} can={can} />)}
    </div>
  );
}
function ApiUserRow({ companyId, reg, gstin, can }: { companyId: string; reg: string; gstin: string; can: boolean }) {
  const { data, setData } = useLoad(() => gstLiveApi.connection(companyId, reg), [companyId, reg]);
  const [f, setF] = useState({ einv_username: "", einv_password: "", ewb_username: "", ewb_password: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [ok, setOk] = useState(false);
  if (!data) return null;
  const save = () => { setBusy(true); setErr(null); setOk(false);
    const p: Record<string, string> = {};
    (["einv_username", "einv_password", "ewb_username", "ewb_password"] as const).forEach((k) => { if (f[k].trim()) p[k] = f[k].trim(); });
    gstLiveApi.saveCreds(companyId, reg, p).then((x) => { setData(x); setF({ einv_username: "", einv_password: "", ewb_username: "", ewb_password: "" }); setOk(true); })
      .catch((x) => setErr(errText(x))).finally(() => setBusy(false)); };
  return (
    <div className="fields inline">
      <b className="mono small">{gstin}</b>
      <label>IRP user<input value={f.einv_username} placeholder={data.einvoice_user ?? ""} disabled={!can} autoComplete="off" onChange={(e) => setF({ ...f, einv_username: e.target.value })} /></label>
      <label>Password<input type="password" value={f.einv_password} placeholder={data.einvoice_password_set ? "Saved (hidden); type to replace" : ""} disabled={!can} autoComplete="new-password" onChange={(e) => setF({ ...f, einv_password: e.target.value })} /></label>
      <label>EWB user<input value={f.ewb_username} placeholder={data.ewaybill_user ?? ""} disabled={!can} autoComplete="off" onChange={(e) => setF({ ...f, ewb_username: e.target.value })} /></label>
      <label>Password<input type="password" value={f.ewb_password} placeholder={data.ewaybill_password_set ? "Saved (hidden); type to replace" : ""} disabled={!can} autoComplete="new-password" onChange={(e) => setF({ ...f, ewb_password: e.target.value })} /></label>
      {can && <button disabled={busy || !Object.values(f).some((x) => x.trim())} onClick={save}>Save</button>}
      {ok && <span className="msg ok small">Saved.</span>}<ErrorNote text={err} />
    </div>
  );
}
